import type { AnonymousCashierAttributionPayload } from '@/services/pay/types';
import { DataStorage, PROMOTER_ATTRIBUTION_SESSION_KEY } from '@wegic/common';

const ANONYMOUS_CASHIER_ATTRIBUTION_SESSION_KEY = 'anonymous_cashier_attribution_params';

interface StoredPromoterAttribution {
  channel?: unknown;
  partnerId?: unknown;
  clickId?: unknown;
  utmSource?: unknown;
  utmMedium?: unknown;
}

interface AnonymousCashierAttributionSources {
  search: string;
  storedPromoterAttribution?: unknown;
  storedTracking?: Partial<Record<keyof AnonymousCashierAttributionPayload, unknown>>;
}

const ATTRIBUTION_KEYS = [
  'refId',
  'im_ref',
  'irpid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ps_partner_key',
  'ps_xid',
] as const satisfies readonly (keyof AnonymousCashierAttributionPayload)[];

function readNonEmptyValue(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  return value.trim() || undefined;
}

function parseStoredPromoterAttribution(value: unknown): StoredPromoterAttribution | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  return value as StoredPromoterAttribution;
}

function getSessionStorage(): Storage | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function buildAnonymousCashierAttributionPayload({
  search,
  storedPromoterAttribution,
  storedTracking = {},
}: AnonymousCashierAttributionSources): AnonymousCashierAttributionPayload {
  try {
    const searchParams = new URLSearchParams(search);
    const storedPromoter = parseStoredPromoterAttribution(storedPromoterAttribution);
    const fallback: Partial<Record<keyof AnonymousCashierAttributionPayload, unknown>> = {
      ...storedTracking,
      utm_source: readNonEmptyValue(storedPromoter?.utmSource) ?? storedTracking.utm_source,
      utm_medium: readNonEmptyValue(storedPromoter?.utmMedium) ?? storedTracking.utm_medium,
    };
    const partnerId = readNonEmptyValue(storedPromoter?.partnerId);
    const clickId = readNonEmptyValue(storedPromoter?.clickId);

    if (partnerId && clickId) {
      if (storedPromoter.channel === 'partnerStack') {
        fallback.ps_partner_key = partnerId;
        fallback.ps_xid = clickId;
      } else if (storedPromoter.channel === 'impact') {
        fallback.irpid = partnerId;
        fallback.im_ref = clickId;
      }
    }

    return ATTRIBUTION_KEYS.reduce<AnonymousCashierAttributionPayload>((payload, key) => {
      const value = readNonEmptyValue(searchParams.get(key)) ?? readNonEmptyValue(fallback[key]);
      if (value) {
        payload[key] = value;
      }
      return payload;
    }, {});
  } catch {
    return {};
  }
}

export function getStoredAnonymousCashierAttribution(
  storage: Storage | undefined = getSessionStorage(),
): AnonymousCashierAttributionPayload {
  if (!storage) {
    return {};
  }

  try {
    const value = storage.getItem(ANONYMOUS_CASHIER_ATTRIBUTION_SESSION_KEY);
    return value
      ? buildAnonymousCashierAttributionPayload({ search: '', storedTracking: JSON.parse(value) })
      : {};
  } catch {
    return {};
  }
}

export function saveAnonymousCashierAttributionIfDetected(
  search = typeof window === 'undefined' ? '' : window.location.search,
  storage: Storage | undefined = getSessionStorage(),
): void {
  if (!storage) {
    return;
  }

  const current = buildAnonymousCashierAttributionPayload({ search });
  if (Object.keys(current).length === 0) {
    return;
  }

  try {
    storage.setItem(
      ANONYMOUS_CASHIER_ATTRIBUTION_SESSION_KEY,
      JSON.stringify({ ...getStoredAnonymousCashierAttribution(storage), ...current }),
    );
  } catch {
    // Attribution must never block app initialization or checkout.
  }
}

function readStoredPromoterAttribution(): unknown {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const value = window.sessionStorage.getItem(PROMOTER_ATTRIBUTION_SESSION_KEY);
    return value ? JSON.parse(value) : undefined;
  } catch {
    return undefined;
  }
}

export function getAnonymousCashierAttributionPayload(): AnonymousCashierAttributionPayload {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = getStoredAnonymousCashierAttribution();
    return buildAnonymousCashierAttributionPayload({
      search: window.location.search,
      storedPromoterAttribution: readStoredPromoterAttribution(),
      storedTracking: {
        ...stored,
        utm_campaign: DataStorage.getField('campaign') || stored.utm_campaign,
        utm_content: DataStorage.getField('ads') || stored.utm_content,
        utm_term: DataStorage.getField('adgroup') || stored.utm_term,
      },
    });
  } catch {
    return {};
  }
}
